import { getTotalClassLevel } from './classIdentity.mjs';

// 2024 Magic Initiate (SRD 5.2.1 Origin feat, repeatable with a different list each time). Each copy of the feat
// names a spell list (feat.spellList) and teaches two cantrips and one level 1 spell from it. The level 1 spell is
// always prepared and can be cast once per Long Rest without a slot, or with any spell slot. The spellcasting ability
// (Intelligence, Wisdom or Charisma) is chosen with the feat. Whenever the character gains a level, one of the chosen
// spells can be replaced with another of the same level from that list.
//
// The choices are saved per spell list in character.featState.magicInitiate[<list>] =
// { ability, cantrips: [id, id], spell: id, changedAtLevel }. Each spell is cast through the
// `feat-magic-initiate-<list>-<spellId>` feature grant.
export const MAGIC_INITIATE_LISTS = Object.freeze({ cleric: 'Cleric', druid: 'Druid', wizard: 'Wizard' });
export const MAGIC_INITIATE_ABILITIES = Object.freeze(['intelligence', 'wisdom', 'charisma']);
export const MAGIC_INITIATE_CANTRIP_COUNT = 2;
export const MAGIC_INITIATE_GRANT_PREFIX = 'feat-magic-initiate-';
export const MAGIC_INITIATE_FREE_CASTS = 1;

const featKey = (feat) => String(feat?.featName ?? feat ?? '').trim().toLowerCase();
const onList = (definition, list) => (definition?.classes || []).map((entry) => String(entry).toLowerCase()).includes(list);

export const magicInitiateGrantId = (list, spellId) => `${MAGIC_INITIATE_GRANT_PREFIX}${list}-${spellId}`;

/** { list, spellId } for a Magic Initiate grant id, or null. */
export const parseMagicInitiateGrant = (grantId) => {
  const id = String(grantId || '');
  if (!id.startsWith(MAGIC_INITIATE_GRANT_PREFIX)) return null;
  const rest = id.slice(MAGIC_INITIATE_GRANT_PREFIX.length);
  const list = Object.keys(MAGIC_INITIATE_LISTS).find((key) => rest.startsWith(`${key}-`));
  return list && rest.length > list.length + 1 ? { list, spellId: rest.slice(list.length + 1) } : null;
};

/** Spell lists of the character's Magic Initiate feats, in feat order. */
export const getMagicInitiateLists = (character) => [...new Set((Array.isArray(character?.feat) ? character.feat : [])
  .filter((feat) => featKey(feat) === 'magic initiate' && MAGIC_INITIATE_LISTS[feat?.spellList])
  .map((feat) => feat.spellList))];

/** The saved choice for one Magic Initiate list, or null when the feat or the choice is missing. */
export const getMagicInitiateChoice = (character, list) => {
  if (!getMagicInitiateLists(character).includes(list)) return null;
  const saved = character?.featState?.magicInitiate?.[list];
  return saved && MAGIC_INITIATE_ABILITIES.includes(saved.ability) ? saved : null;
};

/** Every Magic Initiate spell the character can cast: [{ list, spellId, grantId, ability, level }]. */
export const getMagicInitiateGrants = (character) => getMagicInitiateLists(character).flatMap((list) => {
  const choice = getMagicInitiateChoice(character, list);
  if (!choice) return [];
  const cantrips = (Array.isArray(choice.cantrips) ? choice.cantrips : []).slice(0, MAGIC_INITIATE_CANTRIP_COUNT)
    .map((spellId) => ({ list, spellId, grantId: magicInitiateGrantId(list, spellId), ability: choice.ability, level: 0 }));
  const spell = choice.spell ? [{ list, spellId: choice.spell, grantId: magicInitiateGrantId(list, choice.spell), ability: choice.ability, level: 1 }] : [];
  return [...cantrips, ...spell];
});

/** The grant for a Magic Initiate grant id when the character has chosen that spell, or null. */
export const getMagicInitiateGrant = (character, grantId) => getMagicInitiateGrants(character).find((grant) => grant.grantId === grantId) || null;

/** Free (no-slot) casts of a Magic Initiate level 1 spell left this Long Rest. */
export const magicInitiateFreeCastsRemaining = (character, grantId) => Math.max(0, MAGIC_INITIATE_FREE_CASTS
  - Math.max(0, Math.floor(Number(character?.spellcastingState?.speciesFreeCasts?.[grantId]) || 0)));

/**
 * Validates a new Magic Initiate choice against the catalogue (spell definitions keyed by id) and the saved one.
 * Returns { choice } with normalized ids, or { error }.
 */
export const validateMagicInitiateChoice = (character, list, input, catalogue) => {
  if (!MAGIC_INITIATE_LISTS[list]) return { error: 'Choose Cleric, Druid or Wizard for Magic Initiate.' };
  if (!getMagicInitiateLists(character).includes(list)) return { error: `Take Magic Initiate (${MAGIC_INITIATE_LISTS[list]}) before choosing its spells.` };
  const label = MAGIC_INITIATE_LISTS[list];
  const cantrips = Array.isArray(input?.cantrips) ? input.cantrips.map((id) => String(id).trim().toLowerCase()) : [];
  const spell = String(input?.spell || '').trim().toLowerCase();
  if (cantrips.length !== MAGIC_INITIATE_CANTRIP_COUNT || new Set(cantrips).size !== cantrips.length
    || !cantrips.every((id) => Number(catalogue?.[id]?.level) === 0 && onList(catalogue[id], list))) {
    return { error: `Choose two different ${label} cantrips.` };
  }
  if (Number(catalogue?.[spell]?.level) !== 1 || !onList(catalogue[spell], list)) return { error: `Choose a level 1 ${label} spell.` };
  const previous = character?.featState?.magicInitiate?.[list];
  const level = getTotalClassLevel(character);
  if (!previous) {
    if (!MAGIC_INITIATE_ABILITIES.includes(input?.ability)) return { error: 'Choose Intelligence, Wisdom or Charisma as the spellcasting ability.' };
    return { choice: { ability: input.ability, cantrips, spell, changedAtLevel: level } };
  }
  // The ability is fixed when the feat is chosen; Spell Change replaces one spell per new level.
  if (input?.ability !== undefined && input.ability !== previous.ability) return { error: 'The Magic Initiate spellcasting ability cannot be changed.' };
  const replaced = cantrips.filter((id) => !(previous.cantrips || []).includes(id)).length + (spell === previous.spell ? 0 : 1);
  if (replaced === 0) return { choice: previous, unchanged: true };
  if (level <= Number(previous.changedAtLevel ?? level)) return { error: 'Gain a level before replacing a Magic Initiate spell.' };
  if (replaced > 1) return { error: 'Replace only one Magic Initiate spell per level.' };
  return { choice: { ability: previous.ability, cantrips, spell, changedAtLevel: level } };
};

/** Magic Initiate lists whose spells are not chosen yet. */
export const getUnchosenMagicInitiateLists = (character) => getMagicInitiateLists(character).filter((list) => !getMagicInitiateChoice(character, list));

/** Spell Change: a chosen list can replace one spell after the character gains a level. */
export const canChangeMagicInitiate = (character, list) => {
  const choice = getMagicInitiateChoice(character, list);
  return Boolean(choice) && getTotalClassLevel(character) > Number(choice.changedAtLevel ?? Infinity);
};
